/**
 * Selección de filas y acciones masivas.
 *
 * Aquí hay una distinción que parece de matiz y no lo es (ERP-UX.md §3):
 *
 * - **`ROWS`** — «estas 12 facturas», las que la persona ha marcado.
 * - **`ALL_MATCHING`** — «las 3.481 que cumplen el filtro», incluidas las
 *   que no ha visto porque están fuera de la ventana cargada.
 *
 * Confundirlas es la diferencia entre reclamar doce facturas y reclamar
 * tres mil. Por eso el modo `ALL_MATCHING` **exige un recuento** y nunca se
 * ejecuta sin confirmarlo: quien pulsa tiene que leer un número.
 */

export type SelectionMode = 'ROWS' | 'ALL_MATCHING';

export interface Selection {
  mode: SelectionMode;
  /** En `ROWS`, lo marcado. En `ALL_MATCHING`, lo EXCLUIDO a mano. */
  ids: ReadonlySet<string>;
  /** Ancla del rango para la selección con mayúsculas. */
  anchor: string | null;
  /**
   * La selección tal y como estaba al fijar el ancla.
   *
   * Sin esto, dos rangos seguidos se SUMAN: marcar A, mayúsculas hasta F y
   * mayúsculas hasta C dejaría A-F en lugar de A-C, y quien fuera a borrar
   * tres filas borraría seis. Un rango con mayúsculas **sustituye** al
   * anterior, como en cualquier lista del sistema operativo, y para
   * sustituirlo hay que recordar sobre qué se dibujó.
   */
  base: ReadonlySet<string>;
}

export const EMPTY_SELECTION: Selection = {
  mode: 'ROWS',
  ids: new Set<string>(),
  anchor: null,
  base: new Set<string>(),
};

export function isSelected(selection: Selection, id: string): boolean {
  return selection.mode === 'ROWS' ? selection.ids.has(id) : !selection.ids.has(id);
}

/**
 * Marca o desmarca una fila.
 *
 * Con `shift`, alcanza desde el ancla hasta aquí. El ancla es la última
 * fila pulsada **sin** mayúsculas: así, marcar A, mayúsculas hasta F y
 * mayúsculas hasta C deja marcado A-C, que es lo que la gente espera de
 * cualquier lista.
 */
export function toggleRow(
  selection: Selection,
  id: string,
  visibleIds: readonly string[],
  shift: boolean,
): Selection {
  if (shift && selection.anchor !== null) {
    const from = visibleIds.indexOf(selection.anchor);
    const to = visibleIds.indexOf(id);

    if (from !== -1 && to !== -1) {
      const range = visibleIds.slice(Math.min(from, to), Math.max(from, to) + 1);

      // Se parte de la selección PREVIA al ancla, no de la actual: así el
      // rango nuevo sustituye al anterior en vez de sumarse.
      const ids = new Set(selection.base);

      // El rango toma el sentido de la fila ancla: si el ancla estaba
      // marcada, se marca todo; si no, se desmarca todo. Alternar cada
      // fila del rango daría un resultado imprevisible.
      const marking = isSelected({ ...selection, ids: selection.base }, selection.anchor);
      for (const rangeId of range) {
        const shouldContain = selection.mode === 'ROWS' ? marking : !marking;
        if (shouldContain) ids.add(rangeId);
        else ids.delete(rangeId);
      }

      return { ...selection, ids };
    }
  }

  const ids = new Set(selection.ids);
  if (ids.has(id)) ids.delete(id);
  else ids.add(id);

  // Un clic sin mayúsculas fija el ancla y, con ella, el punto de partida
  // de los rangos que vengan después.
  return { mode: selection.mode, ids, anchor: id, base: ids };
}

/** Marca o desmarca todo lo cargado. No cambia de modo: eso es explícito. */
export function toggleAllVisible(selection: Selection, visibleIds: readonly string[]): Selection {
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => isSelected(selection, id));
  const ids = new Set(selection.ids);

  for (const id of visibleIds) {
    const shouldContain = selection.mode === 'ROWS' ? !allSelected : allSelected;
    if (shouldContain) ids.add(id);
    else ids.delete(id);
  }

  return { ...selection, ids, anchor: null, base: ids };
}

/**
 * Pasa a «todos los que cumplen el filtro».
 *
 * Se ofrece sólo cuando la persona ya ha marcado todo lo visible y hay más
 * cargando detrás: es el momento en que de verdad quiere decir «todos».
 */
export function selectAllMatching(): Selection {
  return { mode: 'ALL_MATCHING', ids: new Set<string>(), anchor: null, base: new Set<string>() };
}

export function clearSelection(): Selection {
  return EMPTY_SELECTION;
}

export interface SelectionSummary {
  /** Cuántas filas afecta la acción. `null` = no se sabe todavía. */
  count: number | null;
  /** Texto para el botón y para la confirmación. */
  label: string;
  /** Una acción sobre un total desconocido no se ejecuta a ciegas. */
  needsCount: boolean;
}

/**
 * Cuántas filas afectaría una acción, y cómo se dice.
 *
 * `totalMatching` es el recuento del servidor. Hoy la API de listados
 * pagina por cursor y **no devuelve total**, así que en modo
 * `ALL_MATCHING` llega `null`: la acción se puede preparar pero no
 * ejecutar, y el aviso lo dice en lugar de inventarse una cifra.
 */
export function summarize(
  selection: Selection,
  totalMatching: number | null,
): SelectionSummary {
  if (selection.mode === 'ROWS') {
    const count = selection.ids.size;
    return {
      count,
      label: count === 1 ? '1 fila seleccionada' : `${count} filas seleccionadas`,
      needsCount: false,
    };
  }

  if (totalMatching === null) {
    return {
      count: null,
      label: 'Todas las que cumplen el filtro (recuento no disponible)',
      needsCount: true,
    };
  }

  const count = Math.max(totalMatching - selection.ids.size, 0);
  const excluded = selection.ids.size > 0 ? `, ${selection.ids.size} excluidas` : '';
  return {
    count,
    label: `${count} filas que cumplen el filtro${excluded}`,
    needsCount: false,
  };
}

/**
 * Los identificadores sobre los que actuar, cuando se pueden enumerar.
 *
 * En `ALL_MATCHING` devuelve `null` a propósito: la acción no se resuelve
 * con una lista de ids del navegador, la tiene que resolver el servidor
 * con el mismo filtro. Mandar los 200 cargados como si fueran «todos»
 * sería aplicar la acción a una fracción y decir que se aplicó a todo.
 */
export function resolveIds(selection: Selection, loadedIds: readonly string[]): string[] | null {
  if (selection.mode === 'ALL_MATCHING') return null;
  return loadedIds.filter((id) => selection.ids.has(id));
}
